import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// ─── Canvas texture generators ────────────────────────────────────────────

const mkSpiralTex = () => {
  const s = 256, c = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx = canvas.getContext("2d");

  // Central bulge
  const bulge = ctx.createRadialGradient(c, c, 0, c, c, 48);
  bulge.addColorStop(0,   "rgba(220,210,255,0.95)");
  bulge.addColorStop(0.4, "rgba(180,160,255,0.40)");
  bulge.addColorStop(1,   "rgba(140,120,240,0)");
  ctx.fillStyle = bulge;
  ctx.fillRect(0, 0, s, s);

  // Two spiral arms
  for (let arm = 0; arm < 2; arm++) {
    const phase = arm * Math.PI;
    for (let t = 0; t < 1; t += 0.0015) {
      const theta = t * Math.PI * 3.2 + phase;
      const rad   = 12 + t * 110;
      const x = c + Math.cos(theta) * rad;
      const y = c + Math.sin(theta) * rad;
      const alpha = (1 - t) * 0.55;
      ctx.beginPath();
      ctx.arc(x, y, 1.4, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(200,185,255,${alpha.toFixed(2)})`;
      ctx.fill();
    }
  }

  // Scattered haze
  for (let i = 0; i < 200; i++) {
    const theta = Math.random() * Math.PI * 2;
    const rad   = Math.random() * 118;
    const x = c + Math.cos(theta) * rad;
    const y = c + Math.sin(theta) * rad;
    const a = (0.5 - rad / 240) * Math.random() * 0.5;
    if (a <= 0) continue;
    ctx.beginPath();
    ctx.arc(x, y, 0.7, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(230,220,255,${a.toFixed(2)})`;
    ctx.fill();
  }

  return new THREE.CanvasTexture(canvas);
};

const mkEllipTex = () => {
  const s = 256, c = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx = canvas.getContext("2d");

  const grad = ctx.createRadialGradient(c, c, 0, c, c, 105);
  grad.addColorStop(0,    "rgba(255,235,180,0.92)");
  grad.addColorStop(0.22, "rgba(255,210,130,0.50)");
  grad.addColorStop(0.50, "rgba(210,150,80,0.16)");
  grad.addColorStop(0.78, "rgba(180,100,40,0.04)");
  grad.addColorStop(1,    "rgba(150,80,20,0)");

  ctx.save();
  ctx.translate(c, c);
  ctx.scale(1.0, 0.62);
  ctx.translate(-c, -c);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, s, s);
  ctx.restore();

  return new THREE.CanvasTexture(canvas);
};

const mkEdgeTex = () => {
  const s = 256, cx = 128, cy = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = s;
  const ctx = canvas.getContext("2d");

  // Central bulge glow
  const bulge = ctx.createRadialGradient(cx, cy, 0, cx, cy, 20);
  bulge.addColorStop(0,   "rgba(255,245,210,0.95)");
  bulge.addColorStop(0.6, "rgba(230,200,160,0.35)");
  bulge.addColorStop(1,   "rgba(200,160,100,0)");
  ctx.fillStyle = bulge;
  ctx.beginPath();
  ctx.arc(cx, cy, 20, 0, Math.PI * 2);
  ctx.fill();

  // Thin disc streak
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1.0, 0.055);
  ctx.translate(-cx, -cy);
  const streak = ctx.createLinearGradient(0, cy, s, cy);
  streak.addColorStop(0,    "rgba(180,175,225,0)");
  streak.addColorStop(0.10, "rgba(205,195,245,0.30)");
  streak.addColorStop(0.35, "rgba(225,218,255,0.62)");
  streak.addColorStop(0.50, "rgba(255,248,220,0.95)");
  streak.addColorStop(0.65, "rgba(225,218,255,0.62)");
  streak.addColorStop(0.90, "rgba(205,195,245,0.30)");
  streak.addColorStop(1,    "rgba(180,175,225,0)");
  ctx.fillStyle = streak;
  ctx.fillRect(0, 0, s, s);
  ctx.restore();

  return new THREE.CanvasTexture(canvas);
};

// ─── Module-level textures (created once) ────────────────────────────────
const SPIRAL_TEX = mkSpiralTex();
const ELLIP_TEX  = mkEllipTex();
const EDGE_TEX   = mkEdgeTex();

// ─── Galaxy & nebula placements ──────────────────────────────────────────
//
//  Distances 3000–7000 units — visible only when zoomed well out past Milky Way
//  Sprites auto-face the camera; texture style conveys galaxy type.
//
const GALAXIES = [
  // Spiral galaxies (blue-purple)
  { tex: SPIRAL_TEX, pos: [ 3500,  420, -2800], size: 420, color: "#b0a0ff", op: 0.72 },
  { tex: SPIRAL_TEX, pos: [-4300, -280,  3200], size: 320, color: "#c0b0ff", op: 0.65 },
  { tex: SPIRAL_TEX, pos: [ 2200,  950, -5600], size: 500, color: "#9898ff", op: 0.68 },
  { tex: SPIRAL_TEX, pos: [-2000, -750,  4900], size: 360, color: "#b8c0ff", op: 0.62 },
  { tex: SPIRAL_TEX, pos: [ 5600,  180, -1400], size: 550, color: "#8898e8", op: 0.58 },
  { tex: SPIRAL_TEX, pos: [-6100, 1050,  2100], size: 460, color: "#a0b0ff", op: 0.62 },
  { tex: SPIRAL_TEX, pos: [ 3100, -1200, 5200], size: 380, color: "#b0c0ff", op: 0.60 },
  { tex: SPIRAL_TEX, pos: [-2800,  600, -6200], size: 480, color: "#a8b8ff", op: 0.65 },

  // Elliptical galaxies (warm gold/orange)
  { tex: ELLIP_TEX,  pos: [-2600, 1250, -4100], size: 280, color: "#ffd888", op: 0.68 },
  { tex: ELLIP_TEX,  pos: [ 4900, -820,  1600], size: 220, color: "#ffb840", op: 0.62 },
  { tex: ELLIP_TEX,  pos: [-3900,  540, -2300], size: 340, color: "#ffe0a0", op: 0.72 },
  { tex: ELLIP_TEX,  pos: [ 6400,  300,  3500], size: 300, color: "#ffc860", op: 0.60 },
  { tex: ELLIP_TEX,  pos: [-5200, -900, -1800], size: 260, color: "#ffd070", op: 0.65 },

  // Edge-on galaxies (cool silver/lavender)
  { tex: EDGE_TEX,   pos: [ 1600,  650, -7100], size: 680, color: "#d8d0ff", op: 0.72 },
  { tex: EDGE_TEX,   pos: [-5600, -420, -3100], size: 580, color: "#c8d0e8", op: 0.68 },
  { tex: EDGE_TEX,   pos: [ 3200,  1550, 5200], size: 520, color: "#d0d8ff", op: 0.62 },
  { tex: EDGE_TEX,   pos: [-4800,  800, -5500], size: 620, color: "#e0d8ff", op: 0.65 },

];

// ─── Comet ────────────────────────────────────────────────────────────────

const COMET_POS = new THREE.Vector3(220, 45, -160);
const TAIL_DIR  = COMET_POS.clone().normalize(); // points away from sun

const CometObject = () => {
  const ref = useRef();

  const comaTex = useMemo(() => {
    const s = 128, c = 64;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = s;
    const ctx = canvas.getContext("2d");
    const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
    grad.addColorStop(0,    "rgba(230,245,255,0.95)");
    grad.addColorStop(0.28, "rgba(190,225,255,0.50)");
    grad.addColorStop(0.60, "rgba(130,190,255,0.14)");
    grad.addColorStop(1,    "rgba(80,140,255,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, s, s);
    return new THREE.CanvasTexture(canvas);
  }, []);

  // Quaternion aligning +Y → TAIL_DIR
  const tailQ = useMemo(() => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), TAIL_DIR);
    return q;
  }, []);

  // Dust tail: slightly off the ion axis (5° tilt in local X)
  const dustQ = useMemo(() => {
    const tilt = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), 0.09);
    return tailQ.clone().multiply(tilt);
  }, [tailQ]);

  const ionGeo = useMemo(() => {
    const len = 30;
    const g = new THREE.CylinderGeometry(0.04, 0.35, len, 8, 1, true);
    g.translate(0, len / 2, 0);
    return g;
  }, []);

  const dustGeo = useMemo(() => {
    const len = 20;
    const g = new THREE.CylinderGeometry(0.08, 1.4, len, 8, 1, true);
    g.translate(0, len / 2, 0);
    return g;
  }, []);

  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.position.y = COMET_POS.y + Math.sin(clock.elapsedTime * 0.07) * 1.8;
    }
  });

  return (
    <group ref={ref} position={COMET_POS.toArray()}>
      {/* Nucleus */}
      <mesh>
        <sphereGeometry args={[0.6, 10, 10]} />
        <meshBasicMaterial color="#e8f5ff" />
      </mesh>

      {/* Coma halo */}
      <sprite scale={[6, 6, 1]}>
        <spriteMaterial
          map={comaTex}
          transparent
          opacity={0.88}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </sprite>

      {/* Ion tail — thin, bright blue */}
      <mesh geometry={ionGeo} quaternion={tailQ}>
        <meshBasicMaterial
          color="#88ccff"
          transparent
          opacity={0.38}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Dust tail — wider, warm yellow, slight angle */}
      <mesh geometry={dustGeo} quaternion={dustQ}>
        <meshBasicMaterial
          color="#ffe8a0"
          transparent
          opacity={0.20}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
};

// ─── CosmicBackground ─────────────────────────────────────────────────────

const CosmicBackground = () => (
  <group>
    {GALAXIES.map((g, i) => (
      <sprite key={i} position={g.pos} scale={[g.size, g.size, 1]}>
        <spriteMaterial
          map={g.tex}
          color={g.color}
          transparent
          opacity={g.op}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </sprite>
    ))}

    <CometObject />
  </group>
);

export default CosmicBackground;
